import { Check, Copy01, Key01 } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useCallback, useId } from 'react';

import { useClipboard } from '@/hooks/useClipboard';
import { Button } from '@/lib/base/buttons/button';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';

export interface WebhookSecretCreatedData {
  bearer_token: string;
  secret_hint: string;
  secret_entity_id: string;
}

export interface WebhookSecretCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: WebhookSecretCreatedData | null;
  /** Optional per-auth-method instruction shown below the token block. */
  instruction?: ReactNode;
}

function CopyAnnouncement({ copied }: { copied: string | boolean }) {
  const message = copied === 'token' ? 'Token copied to clipboard' : '';
  return (
    <span className='sr-only' aria-live='polite' aria-atomic='true'>
      {message}
    </span>
  );
}

export function WebhookSecretCreatedModal({
  isOpen,
  onClose,
  data,
  instruction,
}: WebhookSecretCreatedModalProps) {
  const { copied, copy } = useClipboard();
  const tokenLabelId = useId();

  const handleCopyToken = useCallback(() => {
    if (data) {
      void copy(data.bearer_token, 'token');
    }
  }, [copy, data]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  // Guard AFTER all hooks. data===null while modal is animating closed
  // is normal — render nothing until data is present.
  if (!isOpen || !data) {
    return null;
  }

  const isCopied = copied === 'token';
  const buttonAriaLabel = isCopied ? 'Token copied' : 'Copy token';

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={false}
      isKeyboardDismissDisabled
    >
      <Modal className='max-w-lg bg-bg-primary'>
        <Dialog
          data-instance='webhook-secret-created'
          title='Save your token'
          description="This token is shown once. You won't be able to see it again."
          headerIcon={<Key01 className='size-6 text-fg-brand-primary' />}
          hideCloseButton
          footer={
            <div className='flex justify-end'>
              <Button color='primary' onClick={onClose}>
                Done
              </Button>
            </div>
          }
        >
          <div className='flex flex-col gap-4'>
            <div>
              <label
                id={tokenLabelId}
                className='block text-xs font-medium text-[var(--color-text-secondary)] mb-1'
              >
                Token
              </label>
              <div className='flex items-center gap-2'>
                <code
                  aria-labelledby={tokenLabelId}
                  tabIndex={0}
                  className='flex-1 bg-[var(--color-bg-secondary)] rounded-lg px-3 py-2 font-mono text-xs text-[var(--color-text-primary)] border border-[var(--color-border-secondary)] overflow-x-auto focus-visible:outline-2 focus-visible:outline-[var(--color-brand-600)]'
                >
                  {data.bearer_token}
                </code>
                <Button
                  color='secondary'
                  size='sm'
                  iconLeading={isCopied ? Check : Copy01}
                  onClick={handleCopyToken}
                  aria-label={buttonAriaLabel}
                >
                  {isCopied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
            {instruction && (
              <p className='text-sm text-[var(--color-text-secondary)]'>
                {instruction}
              </p>
            )}
            <CopyAnnouncement copied={copied} />
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
